import React from 'react';
import styles from './EcnOpenOrderPage.module.scss';
import DataGrid, { Column, ColumnChooser, Scrolling, Sorting, StateStoring } from 'devextreme-react/data-grid';
import classNames from 'classnames';
import { TableFooter } from '../../components/TableFooter';
import { Tooltip } from 'devextreme-react/tooltip';
import { HeadTitle } from '../../components/HeadTitle';
import { Header } from '../../components/Header';
import { EcnOpenOrderFilter } from '../EcnOpenOrderFilter';
import { getOpenOrders, downloadOpenOrders } from '../../api/order';
import { Loading } from '../../components/Loading';
import moment from 'moment';
import { formatOrderDate } from '../../util/orderDataRenderFunctions';

// update last part to version you are making changes to cols of grid
const colConfigKey = 'userOpenOrderColumns_3-1-0';

// formatter of number cols 
const formatQty = {
  type: 'fixedPoint',
  precision: 0
};

const formatPrice = {
  type: 'fixedPoint',
  precision: 4
};

const initialState = {
  queryParam: {},
  title: 'ECN Open Order',
  isLoaded: false,
  isReloading: false,
  data: {},
  error: null,
  sort: {
    sortOn: '',
    sortDir: 'asc'
  }
};

var loadingOptions = {
  enabled: false
};

class EcnOpenOrderPage extends React.Component {
  constructor (props) {
    super(props);
    this.dataGridRef = React.createRef();
    let collapseOnSearch = window.localStorage.getItem('tps_collapseSearch') === 'true';
    this.state = {

      ...initialState,
      isDownloadInactive: false,
      isSearching: false,
      columnChooserTooltip: false,
      collapseOnSearch: collapseOnSearch

    };
    this.baseState = this.state;

    this.loadData = this.loadData.bind(this);

    this.setQueryParameters = this.setQueryParameters.bind(this);
    this.handleErrorResults = this.handleErrorResults.bind(this);
    this.clear = this.clear.bind(this);
    this.download = this.download.bind(this);
    this.showColumnChooserOptions = this.showColumnChooserOptions.bind(this);
  }

  showColumnChooserOptions () {
    if (this.dataGrid) {
      this.dataGrid.showColumnChooser();
    }
  }

  toggleColumnChooserTooltip = () => {
    this.setState({
      columnChooserTooltip: !this.state.columnChooserTooltip
    });
  }

  get dataGrid () {
    var gridRef;
    if (this.dataGridRef && this.dataGridRef.current) {
      gridRef = this.dataGridRef.current.instance;
    }
    return gridRef;
  }

  setQueryParameters (queryParamObj, type) {
    queryParamObj.queryDate = moment().format('MM/DD/YYYY HH:mm:ss');
    const isDownload = type === 'download';
    if (isDownload) {
      this.setState({ queryParam: queryParamObj }, this.download);
    } else {
      this.clear();
      // start the loading icon
      this.setState({ isSearching: true, hasMoreError: null });
      // get only specific trades
      this.setState({ queryParam: queryParamObj }, this.loadData);
    }
  }

  loadData () {
    var filters = this.state.queryParam;
    var sort = this.state.sort;
    getOpenOrders(filters, sort)
      .then(data => {
        //  console.log(data);
        this.setState(prevState => ({
          isLoaded: true,
          isReloading: false,
          hasMoreError: data.hasMoreError,
          data: data
        }));
      })
      .catch((error) => {
        // error
        console.log(error);
        var message = (error && error.response &&
          error.response.data && error.response.data) || 'Data currently unavailable';
        if (message && (message.includes('504') || message.includes('Gateway'))) {
          message = 'Query Timed out , Please adjust your filter criteria to return a smaller result set.';
        }
        this.handleErrorResults(message);
      });
  }

  clear () {
    this.setState(this.baseState);
  }

  download () {
    if (this.state.isDownloadInactive) return;
    // make it inactive for future request until current one is solved
    this.setState({ isDownloadInactive: true });
    // download order data
    var filters = this.state.queryParam;
    downloadOpenOrders(filters).then(data => {
      this.setState({ isDownloadInactive: false });
    })
      .catch((err) => {
        // error
        this.setState({ isDownloadInactive: false });
        console.log(err);
      });
  }

  handleErrorResults (errorMessage) {
    this.setState(prevState => ({
      isLoaded: true,
      isReloading: false,
      data: {},
      hasMoreError: null,
      sort: {
        sortOn: '',
        sortDir: 'asc'
      },
      error: errorMessage
    }));
  }

  render () {
    const data = this.state.data;
    const isDataLoaded = this.state.isLoaded && data && data.results && data.results.length > 0;
    const queryDate = this.state.queryParam.queryDate;
    return (
      <div>
        <HeadTitle title={this.state.title} />
        <Header title={this.state.title} />
        <EcnOpenOrderFilter search={this.setQueryParameters}
          isDataLoaded={isDataLoaded}
          isDownloadInactive={this.state.isDownloadInactive}
        />
        {this.state.hasMoreError && this.state.hasMoreError !== '' &&
        <div className={styles.warning}>{this.state.hasMoreError}</div>}

        {this.state.isSearching && <Loading
          type='table'
          data={data.results}
          loaded={this.state.isLoaded}
          reloading={this.state.isReloading}
          error={this.state.error}
          emptyMessage='No results found.' >
          <div>
            <div className={styles.tableHeader}>
              <div>Query executed at {queryDate}</div>
              <div className={styles.tableMenu}>
                <i
                  id='column-chooser'
                  className={classNames(styles.columnChooser, 'dx-icon-columnchooser')}
                  onClick={this.showColumnChooserOptions}
                  onMouseEnter={this.toggleColumnChooserTooltip}
                  onMouseLeave={this.toggleColumnChooserTooltip}
                />
                <Tooltip
                  target={'#column-chooser'}
                  position='left'
                  visible={this.state.columnChooserTooltip}
                  hideOnOutsideClick={false}
                > Open Column Chooser</Tooltip>
              </div>
            </div>
            <DataGrid
              id='ecnOpenOrderGridContainer'
              className={classNames(styles.gridContainer, {
                [styles.collapseGrid]: this.state.collapseOnSearch
              })}
              ref={this.dataGridRef}
              dataSource={data.results}
              allowColumnReordering
              hoverStateEnabled
              columnAutoWidth
              rowAlternationEnabled
              showBorders
              repaintChangesOnly
              loadPanel={loadingOptions}
            >
              <Scrolling mode='infinite' rowRenderingMode='virtual' preloadEnabled />
              <Sorting mode='multiple' />
              <Column dataField='orderDate' caption='Date' calculateDisplayValue={formatOrderDate} allowSorting={false} />
              <Column dataField='orderTime' caption='Entered' />
              <Column dataField='updatedTime' caption='Updated' />
              <Column dataField='mpid' caption='MPID' />
              <Column dataField='symbol' caption='Symbol' />
              <Column dataField='destination' caption='Dest.' />
              <Column dataField='status' caption='Ord Status' />
              <Column dataField='text' caption='Text' />
              <Column dataField='side' caption='Side' />
              <Column dataField='quantity' caption='Qty' format={formatQty} />
              <Column dataField='showQty' caption='Show Qty' format={formatQty} />
              <Column dataField='price' caption='Price' format={formatPrice} />
              <Column dataField='capacity' caption='Capacity' />
              <Column dataField='execQty' caption='Exec Qty' format={formatQty} />
              <Column dataField='linkAtsId' caption='Link ATS ID' />
              <Column dataField='leavesQty' caption='Lvs Qty' format={formatQty} />
              <Column dataField='clientTif' caption='TIF' />

              <Column dataField='clientOrderId' caption='Cl Ord ID' showInColumnChooser={false} />

              <Column dataField='orderId' caption='Ord ID' />
              <Column dataField='orderType' caption='Ord Type' visible={false} />
              <Column dataField='pegType' caption='Peg Type' visible={false} />
              <Column dataField='fixTag2' caption='Exec Inst.' visible={false} />
              <Column dataField='userId' caption='Fix Session' visible={false} />
              <Column dataField='contraOrderId' caption='Contra Ord ID' visible={false} />

              <Column dataField='tradeSession' caption='Trade Session' visible={false} />
              <Column dataField='senderTraderId' caption='Sender Trader' visible={false} />
              <Column dataField='unsolicited' caption='Unsolicited' visible={false} />
              <Column dataField='insideBid' caption='Inside Bid' visible={false} format={formatQty} />
              <Column dataField='insideAsk' caption='Inside Ask' visible={false} format={formatQty} />
              <Column dataField='routeId' caption='Route ID' visible={false} />
              <Column dataField='fixTags' caption='FixTags' visible={false} />
              <Column dataField='nonAffiliate' caption='Non Affiliate' visible={false} />

              <ColumnChooser enabled allowSearch mode='select' title='Select Column(s)' />
              <StateStoring enabled type='localStorage' storageKey={colConfigKey} savingTimeout={10} />
            </DataGrid>
            <TableFooter totalResults={data && data.results && data.results.length} />
            <div id='notes' className={styles.textNoteContainer}>
              <div id='note1' className={styles.textNote}>Messages are ordered by default in the order received from ECN API</div>
              <div id='note2' className={styles.textNote}>* Includes a status generated by the OTC internal systems</div>
            </div>
          </div>
        </Loading> }
      </div>
    );
  }
}
export default EcnOpenOrderPage;
